/**
 * Der Abgleich zwischen dem, was auf dem Gerät liegt, und dem, was der Server anbietet.
 *
 * Reine Rechnung ohne Ablage und ohne Netz — deshalb hier im Fachteil und nicht in der
 * Persistenz. Phase 4 tauscht nur die Quelle der Serverliste aus; diese Funktion bleibt.
 */

/** Wo eine Prüfung zu finden ist. */
export type ExamPresence =
  /** Auf dem Server **und** auf dem Gerät. Der Normalfall. */
  | 'beide'
  /** Nur auf dem Server — herunterladbar. */
  | 'nurServer'
  /**
   * Nur noch auf dem Gerät. Der Server kennt sie nicht mehr: abgeschlossen und entfernt, oder
   * versehentlich gelöscht. Bleibt benutzbar — ein Server, der gerade nichts meldet, darf den
   * laufenden Prüfungsbetrieb nicht anhalten —, wird aber gekennzeichnet.
   */
  | 'nurLokal';

export interface ExamListing {
  id: string;
  name: string;
  meta: string;
  presence: ExamPresence;
  /** S. `ExamRef.einstellungen`. */
  einstellungen?: Record<string, string>;
  /**
   * Der Server trägt eine **jüngere** Fassung als das Gerät.
   *
   * Nur bei `beide` möglich und nur, wenn **beide Seiten** einen Stand nennen — s.
   * `istVeraltet`.
   */
  veraltet: boolean;
  /**
   * S. `ExamRef.simulativ`.
   *
   * ⬅️ **Bis zum 2026-09-20 kam es bei `beide` vom Server** („er ist die Quelle"). Das galt nur,
   * solange jeder Katalog das Feld mitträgt — s. die Regel in `mergeCatalogue`: gesetzt ist es
   * jetzt, sobald **eine** der beiden Seiten es sagt.
   */
  simulativ?: boolean;
  /** S. `ExamRef.vorschau`. */
  vorschau?: boolean;
  /** S. `ExamRef.geschuetzt` — vom Server, für die Abfrage vor dem Herunterladen. */
  geschuetzt?: boolean;
  /** S. `ExamRef.klartextPasswort` — nur Classic, nur im Speicher. */
  klartextPasswort?: string;
}

export interface ExamRef {
  id: string;
  name: string;
  meta: string;
  /**
   * Simulationsprüfung: Schulung, Vorführung, Demo. Die App schickt für sie nichts hinaus.
   *
   * ⚠️ **Muss schon in der Auswahl sichtbar sein**, auch bei einer Prüfung, die noch nicht auf
   * dem Gerät liegt (Vorgabe des Nutzers, 2026-09-18) — deshalb hier und nicht erst in der
   * heruntergeladenen Prüfung.
   */
  simulativ?: boolean;
  /**
   * Kam als Preview über Message. Liegt nur auf dem Gerät und gilt darum nicht als verwaist —
   * wie die Beispielprüfungen.
   */
  vorschau?: boolean;
  /**
   * Was die Prüfung für sich einstellt (Relay `settings`), Name → Wert als Text. Nur bei einer
   * Prüfung auf dem Gerät — der Katalog des Servers trägt das nicht.
   */
  einstellungen?: Record<string, string>;
  /**
   * Wann die Prüfung zuletzt geändert wurde, als Relay-Zeitstempel (`yyyyMMddHHmmssSSS`).
   *
   * Am Gerät ist das `Exam.lastModifiedDate`, am Classic-Server das `lastUpdate` der
   * Katalogzeile auf 17 Stellen gebracht. Relay' `/exam/list` kennt kein solches Feld und
   * lässt es weg — dort wird deshalb nie gewarnt.
   */
  stand?: string;
  /**
   * Die Prüfung hat ein Passwort, das vor dem Herunterladen abgefragt wird (Relay
   * `passwordProtected`, Classic `password` in der Liste; Vorgabe des Nutzers, 2026-10-09).
   */
  geschuetzt?: boolean;
  /**
   * **Nur Classic:** der Klartext aus der Prüfungsliste — die alte App verglich ihn am Gerät.
   * Liegt nur im Speicher der Auswahl, nie in der Ablage. Relay prüft am Server.
   */
  klartextPasswort?: string;
}

/**
 * Ob der Server eine jüngere Fassung hat als das Gerät.
 *
 * ⚠️ **Ein fehlender Stand ist kein „älter".** Relay liefert das Feld nicht, und ältere
 * Classic-Prüfungen haben kein `lastUpdate`. Würde ein leerer Wert als sehr alt gelten, trüge
 * jede solche Prüfung dauerhaft eine Warnung, die der Prüfer nie loswürde — und der einzige
 * Weg, sie loszuwerden, wäre Löschen samt Bewertungen. Lieber stumm als falsch.
 *
 * Beide Formate sind gleich lang und von links nach rechts geordnet, ein Vergleich der
 * Zeichenketten genügt also — dieselbe Annahme wie überall sonst bei Relay-Zeitstempeln.
 */
export function istVeraltet(lokal: string | undefined, server: string | undefined): boolean {
  if (!lokal || !server) return false;
  return server > lokal;
}

/**
 * Lokale und Serverliste zu einer Anzeige zusammenführen.
 *
 * `server === null` heisst „noch nicht nachgesehen" — dann steht **nichts** unter Verdacht:
 * jede lokale Prüfung gilt als `beide`. Das ist die wichtige Unterscheidung zu einer leeren
 * Serverliste, die tatsächlich hiesse „der Server kennt keine einzige davon" und alle
 * lokalen mit einer Warnung versähe. Ein noch nicht gefragter Server darf nicht wie ein
 * antwortender aussehen.
 *
 * Name und Beschreibung kommen bei `beide` vom **Server**: er ist die Quelle, die lokale
 * Kopie kann veraltet sein.
 */
export function mergeCatalogue(
  lokal: ExamRef[],
  server: ExamRef[] | null,
  /**
   * Prüfungen, die nie auf einem Server standen — die Beispielprüfungen (`ladeBeispielpruefungen`).
   * Sie gelten als vorhanden, nicht als verwaist (Gerätelauf 2026-10-02).
   */
  nurAmGeraet: ReadonlySet<string> = new Set(),
): ExamListing[] {
  if (server === null) {
    return lokal.map((e) => ({ ...e, presence: 'beide' as const, veraltet: false }));
  }

  const nachId = new Map(server.map((e) => [e.id, e]));
  const ausLokal: ExamListing[] = lokal.map((e) => {
    const vomServer = nachId.get(e.id);
    // ⚠️ Name und Beschreibung kommen vom Server, der **Stand-Vergleich** braucht aber beide
    // Seiten. Würde hier nur `vomServer` übernommen, ginge der lokale Stand verloren und mit
    // ihm die einzige Grundlage für das Urteil.
    return vomServer
      ? {
          ...vomServer,
          /*
           * ⚠️ **Das Kennzeichen der Simulationsprüfung überlebt das Mischen — in eine
           * Richtung.** Nennt ein Katalog das Feld nicht (alter Server, neue Route, vergessenes
           * Feld), fiele es hier sonst weg, obwohl das Gerät es aus dem heruntergeladenen
           * Dokument längst weiss; der Prüfer sähe keine Warnung.
           *
           * ⚠️ **Die Gegenrichtung gilt ausdrücklich nicht.** Ein alter lokaler Stand darf einer
           * Prüfung, die der Server als Simulation meldet, das Kennzeichen nicht nehmen — das
           * wäre der teurere Fehler. Dieselbe Stelle und dieselbe Regel in der Schwester-App
           * (fairexa Ink, `src/sync/runSync.ts`), am selben Tag gefunden.
           *
           * ℹ️ Entschieden wird hier nichts: die Sperre liest die abgelegte `Exam`-Zeile
           * (`server/sync.ts`), nicht diese Liste. Ein fehlendes Feld kostet ein Etikett,
           * keine Daten.
           */
          simulativ: e.simulativ === true || vomServer.simulativ === true,
          einstellungen: e.einstellungen,
          vorschau: e.vorschau,
          presence: 'beide' as const,
          veraltet: istVeraltet(e.stand, vomServer.stand),
        }
      : { ...e, presence: nurAmGeraet.has(e.id) || e.vorschau ? ('beide' as const) : ('nurLokal' as const), veraltet: false };
  });

  const lokaleIds = new Set(lokal.map((e) => e.id));
  const nurAufServer: ExamListing[] = server
    .filter((e) => !lokaleIds.has(e.id))
    .map((e) => ({ ...e, presence: 'nurServer' as const, veraltet: false }));

  // Erst was auf dem Gerät liegt, dann was noch zu holen wäre: der Prüfer sucht im Regelfall
  // etwas, das schon da ist.
  return [...ausLokal, ...nurAufServer];
}

/**
 * Was die Kopfzeile wissen muss: **dass** etwas ist, nicht **was**.
 *
 * Die Zahlen, nicht die Prüfungen — welche Prüfung es trifft, zeigt die Auswahl. So bleibt die
 * Kopfzeile ein Zeichen und wird nicht zur zweiten Liste.
 */
export interface KatalogLage {
  /** Der letzte Blick auf den Server scheiterte (kein WLAN, Server stumm, Anmeldung fehlt). */
  netzFehler: boolean;
  /** Wie viele geladene Prüfungen am Server eine jüngere Fassung haben. */
  veraltet: number;
  /** Wie viele geladene Prüfungen der Server nicht mehr kennt. */
  verwaist: number;
}

/** Nichts zu melden — auch der Anfangswert, ehe jemand nachgesehen hat. */
export const LAGE_STILL: KatalogLage = { netzFehler: false, veraltet: 0, verwaist: 0 };

/**
 * Die Lage aus der fertigen Anzeige-Liste ablesen.
 *
 * ⚠️ **Der Netzfehler löscht die Zählung nicht.** `mergeCatalogue` behält bei einem
 * gescheiterten Abruf die letzte Serverliste; die Zählung bleibt damit der letzte bekannte
 * Befund. Eine echte Warnung darf nicht verschwinden, nur weil das WLAN kurz wegbricht.
 */
export function lageAus(liste: ExamListing[], netzFehler: boolean): KatalogLage {
  return {
    netzFehler,
    veraltet: liste.filter((e) => e.veraltet).length,
    verwaist: liste.filter((e) => e.presence === 'nurLokal').length,
  };
}
